const { SERVICE, INVITATION, numericStatus } = require("./photographyStatus.cjs");
const CREATOR_HALL_STATUS = Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    READY: "ready",
    EMPTY: "empty",
    ERROR: "error",
    SESSION_EXPIRED: "sessionExpired",
});

const ROLE_META = Object.freeze({
    PHOTOGRAPHY: { roleKey: "photographer", roleLabel: "摄影师", role: "摄影师服务" },
    PHOTOGRAPHER: { roleKey: "photographer", roleLabel: "摄影师", role: "摄影师服务" },
    MAKEUP: { roleKey: "makeup", roleLabel: "化妆师", role: "化妆师服务" },
    MAKEUP_ARTIST: { roleKey: "makeup", roleLabel: "化妆师", role: "化妆师服务" },
});

const INVITATION_LABELS = Object.freeze({ [INVITATION.PENDING]: "待响应", [INVITATION.ACCEPTED]: "已接受", [INVITATION.REJECTED]: "已拒绝", [INVITATION.TIMED_OUT]: "已超时", [INVITATION.WITHDRAWN]: "用户已撤回", [INVITATION.REVIEW_FAILED]: "资格复核未通过", [INVITATION.CANCELLED]: "组合未成立" });

const asText = (value) => value === null || value === undefined ? "" : String(value).trim();
const CREATOR_ORDER_AMOUNT_CONTRACT_ERROR = "服务订单金额字段缺失或无效，请重新加载；如仍失败，请联系平台核对接口版本";
const money = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};
const normalizeServiceOrderAmount = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
};
const formatMoney = (value) => {
    const amount = normalizeServiceOrderAmount(value);
    return amount === null ? "—" : amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const roleMeta = (value, fallback) => {
    const key = asText(value).toUpperCase();
    if (ROLE_META[key]) return ROLE_META[key];
    return fallback === "makeup" ? ROLE_META.MAKEUP : ROLE_META.PHOTOGRAPHY;
};
const parseServerTime = (value) => {
    const text = asText(value);
    if (!text) return NaN;
    return Date.parse(`${text.replace(" ", "T")}+08:00`);
};
const pad = (value) => String(value).padStart(2, "0");
const formatDateLabel = (value) => {
    const timestamp = parseServerTime(value);
    if (!Number.isFinite(timestamp)) return "时间待确认";
    const date = new Date(timestamp + (8 * 60 * 60 * 1000));
    const weekdays = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
    return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日 ${weekdays[date.getUTCDay()]}`;
};
const formatClock = (value) => {
    const text = asText(value);
    return text.length >= 16 ? text.slice(11, 16) : "--:--";
};
const normalizeCreatorReadiness = (raw, fallbackRoleKey = "photographer", fallbackReason = "尚未取得该专业身份") => {
    const source = raw && typeof raw === "object" ? raw : null;
    const meta = roleMeta(source && source.professionalType === 2 ? "MAKEUP" : source && source.professionalType === 1 ? "PHOTOGRAPHY" : fallbackRoleKey, fallbackRoleKey);
    if (!source) {
        return {
            roleKey: meta.roleKey,
            roleLabel: meta.roleLabel,
            professionalIdentityCode: "",
            depositBalance: "0.00",
            frozenAmount: "0.00",
            eligibleForNewOrders: false,
            ineligibleReason: fallbackReason,
            available: false,
        };
    }
    return {
        roleKey: meta.roleKey,
        roleLabel: meta.roleLabel,
        professionalIdentityCode: asText(source.professionalIdentityCode).toUpperCase(),
        depositBalance: money(source.depositBalance),
        frozenAmount: money(source.frozenAmount),
        eligibleForNewOrders: source.eligibleForNewOrders === true,
        ineligibleReason: asText(source.ineligibleReason),
        available: true,
    };
};

const normalizeCreatorJob = (raw = {}) => {
    const meta = roleMeta(raw.serviceType);
    const serviceStartAt = asText(raw.serviceStartAt);
    const expectedArrivalAt = asText(raw.expectedArrivalAt);
    const serviceOrderAmount = normalizeServiceOrderAmount(raw.serviceOrderAmount);
    const parsedPeopleCount = Number(raw.peopleCount);
    const peopleCount = Number.isInteger(parsedPeopleCount) && parsedPeopleCount > 0 ? parsedPeopleCount : null;
    const themeTypeName = asText(raw.themeTypeName);
    const themeName = asText(raw.themeName);
    const styleName = asText(raw.styleName);
    const themeParts = [themeTypeName, themeName, styleName].filter(Boolean);
    return {
        id: asText(raw.serviceOrderNo),
        serviceOrderNo: asText(raw.serviceOrderNo),
        serviceType: asText(raw.serviceType),
        roleKey: meta.roleKey,
        role: meta.role,
        serviceStatus: numericStatus(raw.serviceStatus, SERVICE),
        serviceStartAt,
        expectedArrivalAt,
        locationSummary: asText(raw.locationSummary),
        themeTypeName,
        themeName,
        styleName,
        packageName: asText(raw.packageName),
        peopleCount,
        publicAcceptanceDeadlineAt: asText(raw.publicAcceptanceDeadlineAt),
        serviceOrderAmount,
        contractError: serviceOrderAmount === null ? CREATOR_ORDER_AMOUNT_CONTRACT_ERROR : "",
        dateValue: serviceStartAt.slice(0, 10),
        date: formatDateLabel(serviceStartAt),
        time: formatClock(serviceStartAt),
        expectedArrivalText: expectedArrivalAt ? `${formatClock(expectedArrivalAt)} 建议到场` : "建议到场时间待确认",
        theme: themeParts.join(" · ") || "服务主题待确认",
        people: Number.isInteger(peopleCount) && peopleCount > 0 ? `${peopleCount} 人` : null,
        deadlineAt: asText(raw.publicAcceptanceDeadlineAt),
        isInvitation: false,
    };
};

const normalizeCreatorInvitation = (raw = {}) => {
    const serviceSummary = normalizeCreatorJob(raw);
    const invitationStatus = numericStatus(raw.invitationStatus, INVITATION);
    const waitingForCounterpart = invitationStatus === INVITATION.ACCEPTED && raw.invitationPrerequisitePending === true;
    return {
        ...serviceSummary,
        invitationStatus,
        statusLabel: invitationStatus === INVITATION.ACCEPTED ? "待服务" : (INVITATION_LABELS[invitationStatus] || "状态待确认"),
        invitationResponseDeadline: asText(raw.invitationResponseDeadline),
        invitationPrerequisitePending: raw.invitationPrerequisitePending === true,
        waitingReason: asText(raw.waitingReason),
        waitingHint: waitingForCounterpart ? "等待另一位创作者接受邀约" : "",
        deadlineAt: asText(raw.invitationResponseDeadline),
        isPending: invitationStatus === INVITATION.PENDING,
        waitingForCounterpart,
        isInvitation: true,
    };
};

const formatCountdown = (deadline, now = Date.now()) => {
    const remaining = Math.max(0, Math.floor((parseServerTime(deadline) - now) / 1000));
    if (!remaining) return "已截止";
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const getErrorStatus = (error) => {
    if (error && error.response && error.response.data && error.response.data.code !== undefined) return Number(error.response.data.code);
    if (error && error.response && error.response.status !== undefined) return Number(error.response.status);
    if (error && error.code !== undefined) return Number(error.code);
    return 0;
};
const getCreatorHallRequestState = (error) => getErrorStatus(error) === 401
    ? CREATOR_HALL_STATUS.SESSION_EXPIRED
    : CREATOR_HALL_STATUS.ERROR;

const canAcceptCreatorJob = order => Boolean(order && order.serviceStatus === SERVICE.WAITING_ACCEPTANCE);

module.exports = {
    canAcceptCreatorJob,
    CREATOR_ORDER_AMOUNT_CONTRACT_ERROR,
    CREATOR_HALL_STATUS,
    formatCountdown,
    formatMoney,
    getCreatorHallRequestState,
    getErrorStatus,
    normalizeCreatorInvitation,
    normalizeCreatorJob,
    normalizeCreatorReadiness,
};
